/** Profile + correction → PaneConfig (docs/OPTICS.md §6–7; expert feedback: docs/FEEDBACK.md, DECISIONS D-011…D-022). */

import { accommodationAmplitude, progressiveAddByAge } from './eye';
import { retinalMar } from './retina';
import type { FocalComponent, Hoa, PaneConfig } from './types';

export type Stage3 = 0 | 1 | 2 | 3;
export type Stage4 = 0 | 1 | 2 | 3 | 4;
export type Stage5 = 0 | 1 | 2 | 3 | 4 | 5;

export interface Profile {
  age: number;
  /** Sphere, D (autorefractometer SPH). */
  sph: number;
  /** Cylinder in minus form, D (≤ 0); plus-cylinder input is transposed before it gets here. */
  cyl: number;
  /** Axis of the minus cylinder, degrees 1–180. */
  axis: number;
  /** No crystalline lens (D-021). */
  aphakia: boolean;
  cataract: Stage4;
  keratoconus: Stage4;
  glaucoma: Stage4;
  amd: Stage3;
  dr: Stage3;
  /** Hypertensive retinopathy (D-022). */
  hr: Stage4;
  /** Retinitis pigmentosa (D-012). */
  rp: Stage4;
  /** Amblyopia degree, 5-degree CIS classification (D-020). */
  amblyopia: Stage5;
}

export type BaseCorrectionId =
  | 'none' | 'glasses' | 'progressive' | 'rgp' | 'lasik' | 'icl'
  | 'iol_mono' | 'iol_monoplus' | 'iol_edof' | 'iol_tri';

/** Toric lenses are separate list entries (expert feedback, D-011). */
export type CorrectionId =
  | BaseCorrectionId
  | 'icl_toric' | 'iol_mono_toric' | 'iol_monoplus_toric' | 'iol_edof_toric' | 'iol_tri_toric';

export const CORRECTION_IDS: CorrectionId[] = [
  'none', 'glasses', 'progressive', 'rgp', 'lasik', 'icl', 'icl_toric',
  'iol_mono', 'iol_mono_toric', 'iol_monoplus', 'iol_monoplus_toric',
  'iol_edof', 'iol_edof_toric', 'iol_tri', 'iol_tri_toric',
];

export interface CorrectionChoice {
  id: CorrectionId;
  /** Toric version; also implied by a `*_toric` id. */
  toric: boolean;
  /** Monofocal IOL target. */
  monoTarget: 'far' | 'near';
}

export function isToric(id: CorrectionId): boolean {
  return id.endsWith('_toric');
}

export function baseCorrection(id: CorrectionId): BaseCorrectionId {
  return (isToric(id) ? id.slice(0, -'_toric'.length) : id) as BaseCorrectionId;
}

export function isIol(id: CorrectionId): boolean {
  return id.startsWith('iol_');
}

export function isMultifocal(id: CorrectionId): boolean {
  const b = baseCorrection(id);
  return b === 'iol_tri' || b === 'iol_edof' || b === 'iol_monoplus';
}

interface CataractRow {
  scatter: number; c31h: number; c33s: number; c31add: number;
  transmission: [number, number, number]; nuclearShift: number; marCat: number;
}

const CATARACT: Record<Exclude<Stage4, 0>, CataractRow> = {
  1: { scatter: 0.12, c31h: 0.12, c33s: 0.10, c31add: 0.08, transmission: [0.95, 0.91, 0.83], nuclearShift: 0, marCat: 0.9 },
  2: { scatter: 0.30, c31h: 0.24, c33s: 0.20, c31add: 0.16, transmission: [0.90, 0.82, 0.66], nuclearShift: -0.5, marCat: 2.2 },
  3: { scatter: 0.60, c31h: 0.36, c33s: 0.30, c31add: 0.24, transmission: [0.85, 0.73, 0.49], nuclearShift: -1.0, marCat: 8 },
  4: { scatter: 0.85, c31h: 0.48, c33s: 0.40, c31add: 0.32, transmission: [0.80, 0.64, 0.32], nuclearShift: -1.5, marCat: 30 },
};

const KERATOCONUS: Record<Exclude<Stage4, 0>, { c31: number; c33: number; c40: number }> = {
  1: { c31: -1.0, c33: 0.3, c40: -0.15 },
  2: { c31: -2.0, c33: 0.5, c40: -0.30 },
  3: { c31: -3.2, c33: 0.8, c40: -0.50 },
  4: { c31: -4.5, c33: 1.1, c40: -0.70 },
};

/** Corneal spherical aberration of an eye without a crystalline lens, µm for a 6 mm pupil (D-021). */
const CORNEAL_C40 = 0.27;
/** Retinal image magnification of aphakic spectacles and aphakic contact lenses (D-021). */
export const APHAKIC_GLASSES_MAG = 1.28;
export const APHAKIC_CONTACT_MAG = 1.08;
/** Near addition of aphakic bifocal / progressive glasses, D: there is no accommodation at any age. */
export const APHAKIC_ADD = 3.0;

/** Starburst streak energy (D-018): suture rays of the natural lens, cortical spokes, corneal irregularity, devices. */
const STREAK_CATARACT = [0, 0.004, 0.008, 0.012, 0.016] as const;

const SINGLE: FocalComponent[] = [{ w: 1, add: 0, diffractive: false }];

function zonal(addCentre: number, zoneRadius: number): FocalComponent[] {
  const profile = (r: number): number => (r < zoneRadius ? addCentre * (1 - (r / zoneRadius) ** 2) : 0);
  return [
    { w: 1, add: 0, diffractive: false, r0: 0, r1: zoneRadius, profile },
    { w: 1, add: 0, diffractive: false, r0: zoneRadius, r1: Infinity },
  ];
}

const TRIFOCAL: FocalComponent[] = [
  { w: 0.44, add: 0, diffractive: true },
  { w: 0.21, add: 1.5, diffractive: true },
  { w: 0.25, add: 2.5, diffractive: true },
];
const TRIFOCAL_LOST = 0.10;

function combineScatter(parts: number[]): number {
  let keep = 1;
  for (const f of parts) keep *= 1 - f;
  return 1 - keep;
}

function round(x: number, digits = 4): number {
  const m = 10 ** digits;
  return Math.round(x * m) / m;
}

/**
 * Build the optical configuration of one pane.
 * @param pupil pupil diameter for the current scene lighting, mm (see eye.pupilDiameter).
 */
export function buildPaneConfig(p: Profile, choice: CorrectionChoice, pupil: number): PaneConfig {
  const base = baseCorrection(choice.id);
  const toric = choice.toric || isToric(choice.id);
  const iol = isIol(choice.id);
  const aphakic = p.aphakia;
  const ageOver20 = Math.max(0, p.age - 20);
  const S = p.sph;
  const C = Math.min(0, p.cyl);
  const SE = S + C / 2;
  const cat = !aphakic && p.cataract !== 0 ? CATARACT[p.cataract] : null;
  const kc = p.keratoconus !== 0 ? KERATOCONUS[p.keratoconus] : null;

  // Baseline eye (OPTICS §7). Without a crystalline lens only the cornea is left (D-021).
  const hoa: Hoa = {
    c40: aphakic ? CORNEAL_C40 : 0.06 + 0.002 * ageOver20,
    c31: 0.07,
    c31h: 0,
    c33: 0.05,
    c33s: 0,
  };
  const scatterParts: number[] = [aphakic ? 0.03 : 0.03 + 0.0012 * ageOver20];
  const transmission: [number, number, number] = aphakic ? [1, 1, 1] : [1, 1, 1 - 0.003 * ageOver20];
  let marCat = 0;
  let nuclearShift = 0;

  if (cat) {
    scatterParts.push(cat.scatter);
    hoa.c31h += cat.c31h;
    hoa.c33s += cat.c33s;
    hoa.c31 += cat.c31add;
    transmission[0] *= cat.transmission[0];
    transmission[1] *= cat.transmission[1];
    transmission[2] *= cat.transmission[2];
    marCat = cat.marCat;
    nuclearShift = cat.nuclearShift;
  }

  // Corneal keratoconus aberrations stay after every correction except RGP (× 0.15).
  const kcScale = base === 'rgp' ? 0.15 : 1;
  if (kc) {
    hoa.c31 += kc.c31 * kcScale;
    hoa.c33 += kc.c33 * kcScale;
    hoa.c40 += kc.c40 * kcScale;
  }

  const STotal = S + nuclearShift;
  let resS = 0;
  let resC = 0;
  let amplitude = aphakic ? 0 : accommodationAmplitude(p.age);
  let components: FocalComponent[] = SINGLE;
  let progressiveAdd = 0;
  let iclRing = false;
  let magnification = 1;
  let streakDevice = 0;

  switch (base) {
    case 'none':
      resS = STotal;
      resC = C;
      break;
    case 'glasses':
      if (aphakic) magnification = APHAKIC_GLASSES_MAG;
      break;
    case 'rgp':
      if (aphakic) magnification = APHAKIC_CONTACT_MAG;
      break;
    case 'progressive':
      progressiveAdd = aphakic ? APHAKIC_ADD : progressiveAddByAge(p.age);
      if (aphakic) magnification = APHAKIC_GLASSES_MAG;
      break;
    case 'lasik':
      hoa.c40 += SE < 0 ? 0.045 * Math.abs(SE) : -0.06 * SE;
      scatterParts.push(0.02);
      streakDevice = 0.004;
      break;
    case 'icl':
      resC = toric ? 0 : C;
      iclRing = true;
      streakDevice = 0.003;
      break;
    case 'iol_mono':
    case 'iol_monoplus':
    case 'iol_edof':
    case 'iol_tri': {
      // The crystalline lens is replaced or, in aphakia, an IOL is implanted (OPTICS §6). D-003 for coma/trefoil.
      scatterParts.length = 0;
      scatterParts.push(0.03);
      transmission[0] = transmission[1] = transmission[2] = 1;
      marCat = 0;
      hoa.c40 = 0.05 + (kc ? kc.c40 * kcScale : 0);
      hoa.c31h = 0;
      hoa.c33s = 0;
      amplitude = 0;
      resC = toric ? -0.25 : C;
      if (base === 'iol_mono') {
        resS = choice.monoTarget === 'near' ? -2.5 : 0;
      } else if (base === 'iol_monoplus') {
        components = zonal(0.8, 1.25);
      } else if (base === 'iol_edof') {
        components = zonal(1.6, 1.1);
        streakDevice = 0.001;
      } else {
        components = TRIFOCAL;
        scatterParts.push(TRIFOCAL_LOST);
        streakDevice = 0.002;
      }
      break;
    }
  }

  const naturalLens = !aphakic && !iol;
  const lensStreak = naturalLens ? 0.002 + 0.00005 * ageOver20 + (cat ? STREAK_CATARACT[p.cataract] : 0) : 0.0015;
  const corneaStreak = kc ? 0.003 * p.keratoconus * (base === 'rgp' ? 0.3 : 1) : 0;

  const cfg: Omit<PaneConfig, 'key'> = {
    age: p.age,
    pupil: round(pupil, 3),
    amplitude: round(amplitude),
    resS: round(resS),
    resC: round(resC),
    axis: p.axis,
    progressiveAdd,
    components,
    hoa: {
      c40: round(hoa.c40), c31: round(hoa.c31), c31h: round(hoa.c31h), c33: round(hoa.c33), c33s: round(hoa.c33s),
    },
    scatter: round(combineScatter(scatterParts)),
    transmission: [round(transmission[0]), round(transmission[1]), round(transmission[2])],
    marCat,
    marRet: retinalMar(p),
    iclRing,
    magnification,
    streaks: round(lensStreak + corneaStreak + streakDevice, 5),
    streakPattern: naturalLens ? 1 : 2,
  };
  const key = [
    base, toric ? 'T' : '', cfg.age, cfg.pupil, cfg.amplitude, cfg.resS, cfg.resC, cfg.axis, cfg.progressiveAdd,
    cfg.hoa.c40, cfg.hoa.c31, cfg.hoa.c31h, cfg.hoa.c33, cfg.hoa.c33s,
    cfg.scatter, cfg.transmission.join(','), cfg.marCat, cfg.marRet, cfg.iclRing ? 1 : 0,
    cfg.magnification, cfg.streaks, cfg.streakPattern,
  ].join('|');
  return { ...cfg, key };
}
